import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { ctrReachability, POWER_GATE_VIEWS } from '../utils/ctr-reachability';
import { funnelUnits } from '../utils/funnel-units';
import DeltaBadge from '../components/DeltaBadge';
import type {
  UserAnalyticsResponse,
  AnalyticsDailyPoint,
  AnalyticsFeedRow,
  AnalyticsPeriod,
  MeasuredEarningsResponse,
} from '../services/apiService';

// Day-granularity periods (analytics-period-ranges).
const PERIODS: { key: AnalyticsPeriod; label: string; short: string }[] = [
  { key: 'today', label: 'Today', short: 'Today' },
  { key: 'yesterday', label: 'Yesterday', short: 'Yesterday' },
  { key: 'week', label: 'This week', short: 'This week' },
  { key: '30d', label: 'Last 30 days', short: '30d' },
  { key: '90d', label: 'Last 90 days', short: '90d' },
];
function periodLabelFor(p: AnalyticsPeriod): string {
  return PERIODS.find(x => x.key === p)?.label ?? p;
}
// The selected period survives a refresh. A stored value that is not a known
// period key (stale build, hand-edited storage) must fall back to the default
// rather than reach the API, and storage access can throw (private mode).
const PERIOD_STORAGE_KEY = 'imgaff_analytics_period';
function initialPeriod(): AnalyticsPeriod {
  try {
    const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
    if (PERIODS.some(p => p.key === stored)) return stored as AnalyticsPeriod;
  } catch { /* storage unavailable */ }
  return '30d';
}
function rememberPeriod(p: AnalyticsPeriod): void {
  try { window.localStorage.setItem(PERIOD_STORAGE_KEY, p); } catch { /* best-effort */ }
}
// -----------------------------------------------------------------------
// Styling — design-system tokens (design-system/foundations/colors.html):
// accent var(--brand), muted var(--muted), border var(--line), good var(--good). No chart
// libraries: one clean inline-SVG multi-series line chart.
// -----------------------------------------------------------------------

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius)',
  padding: '1rem 1.25rem',
  background: 'var(--surface)',
};

const errorBannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '1rem',
  padding: '1rem',
  backgroundColor: 'var(--danger-bg)',
  color: 'var(--danger)',
  borderRadius: 'var(--radius)',
  border: '1px solid rgba(180,35,24,0.24)',
};

const retryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  backgroundColor: 'var(--danger)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// Screen-reader-only text (the classic clip pattern) — content stays in the
// accessibility tree without occupying visual space.
const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// Owner decision 2026-07-20: show CTR early (labeled as still-collecting)
// rather than a dash — same-window math already prevents absurdities; this
// tiny floor only stops a single view from rendering as "100%". Stays here
// because it governs DISPLAY; POWER_GATE_VIEWS governs the verdict and moved
// to utils/ctr-reachability with the arithmetic that reads it.
const MIN_CTR_DISPLAY_VIEWS = 10;

// -----------------------------------------------------------------------
// The one funnel vocabulary (portal-ux-clarity): Pageviews / Overlays
// delivered / Overlays viewed / Product clicks — identical on Dashboard.
// -----------------------------------------------------------------------

type SeriesKey = 'impressions' | 'matchesServed' | 'views' | 'clicks';

interface SeriesDef {
  key: SeriesKey;
  label: string;
  color: string;
  // matchesServed/views are metered daily counters (approximate);
  // impressions (beacon) and clicks (persisted rows) are not marked.
  approximate: boolean;
}

// Literal hexes on purpose: these feed an SVG `stroke` presentation
// attribute, which does not resolve CSS custom properties in every browser.
// The four hues are the brand palette's chips plus brand blue, so they stay
// distinguishable from each other and consistent with the rest of the portal.
const FUNNEL_SERIES: SeriesDef[] = [
  { key: 'impressions', label: 'Pageviews', color: '#FF8904', approximate: false },
  { key: 'matchesServed', label: 'Overlays delivered', color: '#76CE29', approximate: true },
  { key: 'views', label: 'Overlays viewed', color: '#717171', approximate: true },
  { key: 'clicks', label: 'Product clicks', color: '#0178FE', approximate: false },
];

// impressions is optional in the contract (older rows predate the beacon)
function seriesValue(point: AnalyticsDailyPoint, key: SeriesKey): number {
  return key === 'impressions' ? (point.impressions ?? 0) : point[key];
}

// -----------------------------------------------------------------------
/** A count and its noun, separated and agreed — the page's other ten figures already are. */
const plural = (n: number, noun: string) => `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;

// Funnel tiles: four raw counts + CTR. The CTR tile, its view-tracking
// cutoff note, and the power-gate collecting state are decision-critical
// STATUS — they stay visible, never folded into the disclosure.
// -----------------------------------------------------------------------

interface FunnelSectionProps {
  daily: AnalyticsDailyPoint[];
  viewTrackingStartedAt: string | null;
  periodLabel: string;
}

const FunnelSection: React.FC<FunnelSectionProps> = ({ daily, viewTrackingStartedAt, periodLabel }) => {
  const serves = daily.reduce((s, d) => s + d.matchesServed, 0);
  const views = daily.reduce((s, d) => s + d.views, 0);
  const clicks = daily.reduce((s, d) => s + d.clicks, 0);
  const impressions = daily.reduce((s, d) => s + (d.impressions ?? 0), 0);
  // CTR is SAME-WINDOW math only, and STRICTLY AFTER the cutoff DAY: view
  // tracking starts at a timestamp mid-day, so the cutoff day itself has
  // whole-day clicks but only post-deployment views — including it inflates
  // CTR (analytics-period-ranges / codex). Excluding days <= cutoffDay from
  // BOTH the numerator and denominator closes that, and a single-day period
  // on/before the cutoff day naturally yields 0 CTR-views → unavailable.
  const cutoffDay = viewTrackingStartedAt ? viewTrackingStartedAt.slice(0, 10) : null;
  const ctrClicks = cutoffDay
    ? daily.filter(d => d.day > cutoffDay).reduce((s, d) => s + d.clicks, 0)
    : 0;
  const ctrViews = cutoffDay
    ? daily.filter(d => d.day > cutoffDay).reduce((s, d) => s + d.views, 0)
    : 0;
  // The four sub-lines are shared with the Dashboard, which sums this same daily
  // series to the same four numbers — see utils/funnel-units. The post-cutoff
  // pairing those ratios need (the 2000%-CTR lesson: views exist only since the
  // cutoff) moved there with them, which is why it no longer appears here.
  const units = funnelUnits({ daily, viewTrackingStartedAt, periodLabel });
  const ctrAvailable = cutoffDay != null && ctrViews >= MIN_CTR_DISPLAY_VIEWS;
  const ctr = ctrAvailable ? (ctrClicks / ctrViews) * 100 : null;
  // Advice about collecting more has to know how long the window is: on a
  // single-day period it resets at midnight, so "6,488 to go" is unreachable
  // by construction rather than merely distant.
  const reachability = ctrReachability(views, daily.length);
  const tile = (label: string, value: string, sub: string, testId: string) => (
    <div style={{ ...cardStyle, padding: '0.8rem 1rem', minWidth: 0 }} data-testid={testId}>
      <div style={{ fontFamily: 'var(--display)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: '0.56px' }}>{label}</div>
      <div style={{ fontFamily: 'var(--display)', fontSize: '1.45rem', fontWeight: 600, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>{sub}</div>
    </div>
  );

  return (
    <div style={{ marginBottom: '1rem' }} data-testid="funnel-section">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '0.5rem' }}>
        {tile('Pageviews', impressions.toLocaleString(), units.pageviews, 'funnel-impressions')}
        {tile('Overlays delivered', serves.toLocaleString(), units.delivered, 'funnel-serves')}
        {tile('Overlays viewed', views.toLocaleString(), units.viewed, 'funnel-views')}
        {tile('Product clicks', clicks.toLocaleString(), units.clicks, 'funnel-clicks')}
        {tile(
          'CTR (product clicks / overlays viewed)',
          ctr != null ? `${ctr.toFixed(2)}%` : '—',
          ctr != null
            ? ctrViews < POWER_GATE_VIEWS
              ? 'early estimate — still collecting'
              : 'product clicks / overlays viewed — the number that matters'
            : cutoffDay
              ? `collecting — ${views.toLocaleString()} overlay view${views === 1 ? '' : 's'} so far`
              : 'collecting — no overlay view data yet',
          'funnel-ctr'
        )}
      </div>
      {/* NOTHING AT ALL IS A DIFFERENT ANSWER FROM A SMALL NUMBER, and this
          screen had none for it. Driven 2026-09-07 with an account that has
          reported nothing: five tiles reading 0, and — measured — ZERO links
          anywhere on the page. This screen's recorded Decision (2026-09-05) is
          that it "does not act, it points"; in the one state where a publisher
          has nothing to read and needs pointing most, it pointed nowhere.
          That Decision named this as the sub-question a pass may still raise.

          It is also the state that cannot tell itself apart: a widget that is
          not installed and a widget on a quiet page produce the same five
          zeros. The screen that owns the difference is Install, so that is
          where this sends them — it does not diagnose, because diagnosing is
          the other screen's job.

          Gated on the WIDEST funnel stage. Pageviews are counted before any
          matching, serving or viewing happens, so `impressions === 0` means
          nothing reached us at all; any of the narrower counts being zero is an
          ordinary outcome that the tiles already report honestly. */}
      {impressions === 0 && (
        <p className="no-data" data-testid="funnel-nothing-yet" style={{ marginTop: '0.25rem' }}>
          Nothing has reached us for {periodLabel.toLowerCase()} yet. If you have just
          installed, allow a few minutes and check back — otherwise the{' '}
          <Link to="/embed">Install page</Link> shows whether your key and snippet are live.
        </p>
      )}

      {cutoffDay != null && views > 0 && ctrViews < POWER_GATE_VIEWS && (
        <p className="no-data" data-testid="funnel-power-gate" style={{ marginTop: '0.25rem' }}>
          Not enough data for a reliable verdict yet: {views.toLocaleString()} of ~{POWER_GATE_VIEWS.toLocaleString()} overlays
          viewed collected ({Math.max(0, POWER_GATE_VIEWS - views).toLocaleString()} to go).{' '}
          {reachability.text}
        </p>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// "About these metrics" — the ONE caveat disclosure for the page. Native
// button, aria-expanded/aria-controls, ~44px target, content in the DOM
// when expanded, Escape closes and returns focus to the button.
// -----------------------------------------------------------------------

const AboutMetricsDisclosure: React.FC<{ viewTrackingStartedAt?: string | null }> = ({ viewTrackingStartedAt }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      style={{ marginBottom: '1.25rem' }}
      onKeyDown={e => {
        if (e.key === 'Escape' && open) {
          setOpen(false);
          buttonRef.current?.focus();
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        data-testid="about-metrics-toggle"
        aria-expanded={open}
        aria-controls="about-metrics-content"
        onClick={() => setOpen(o => !o)}
        className="btn btn--secondary"
      >
        About these metrics {open ? '▲' : '▼'}
      </button>
      {open && (
        <div
          id="about-metrics-content"
          data-testid="about-metrics-content"
          style={{ ...cardStyle, marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--ink-soft)' }}
        >
          <ul style={{ margin: 0, paddingLeft: '1.2rem', display: 'grid', gap: '0.35rem' }}>
            <li><strong>Pageviews</strong> — page loads where your widget ran (impression beacon).</li>
            <li><strong>Overlays delivered</strong> — matched overlays served onto images. Approximate metered counter.</li>
            <li><strong>Overlays viewed</strong> — overlays at least 50% visible for 1 second. Approximate metered counter.</li>
            <li><strong>Product clicks</strong> — clicks through our redirect; your network's accepted-click count may differ.</li>
            <li>Counts are independent tallies and may not line up perfectly — this is not a guaranteed monotone funnel.</li>
            <li>Overlays viewed (and therefore CTR) depend on your widget's display trigger setting — CTR is comparable within a display mode, not across modes.</li>
            {viewTrackingStartedAt && (
              <li>Overlay-view tracking started {new Date(viewTrackingStartedAt).toLocaleDateString()}; earlier days have no overlay-view data, and overlay-view-based ratios pair only same-period counts.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// The one daily chart: four raw-count series, toggleable legend. The
// y-scale derives from VISIBLE series only; series are never independently
// normalized (shared scale keeps magnitudes honest).
// -----------------------------------------------------------------------

interface FunnelChartProps {
  data: AnalyticsDailyPoint[];
  periodLabel: string;
}

const FunnelChart: React.FC<FunnelChartProps> = ({ data, periodLabel }) => {
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    impressions: true,
    matchesServed: true,
    views: true,
    clicks: true,
  });

  const width = 600;
  const height = 180;
  const padding = 20;

  const visibleSeries = FUNNEL_SERIES.filter(s => visible[s.key]);
  // Shared y-scale from visible series only
  const max = Math.max(1, ...visibleSeries.flatMap(s => data.map(d => seriesValue(d, s.key))));
  const stepX = data.length > 1 ? (width - padding * 2) / (data.length - 1) : 0;

  // Coordinates once, used by BOTH the line and the markers below.
  const pointsOf = (key: SeriesKey) =>
    data.map((d, i) => ({
      // One day sits in the MIDDLE. stepX is 0 for a single point, so the
      // honest `padding + i * stepX` pins it against the left edge, where it
      // reads as a rendering artifact rather than as that day's value.
      x: data.length === 1 ? width / 2 : padding + i * stepX,
      y: height - padding - (seriesValue(d, key) / max) * (height - padding * 2),
    }));

  const linePoints = (key: SeriesKey) =>
    pointsOf(key).map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  const totals = FUNNEL_SERIES.map(s => ({
    ...s,
    total: data.reduce((sum, d) => sum + seriesValue(d, s.key), 0),
  }));

  return (
    <div style={{ ...cardStyle, marginBottom: '1.5rem' }} data-testid="funnel-chart">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <h4 style={{ margin: 0 }}>Daily funnel</h4>
        <div role="group" aria-label="Toggle chart series" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {totals.map(s => (
            <button
              key={s.key}
              type="button"
              data-testid={`series-toggle-${s.key}`}
              aria-pressed={visible[s.key]}
              onClick={() => setVisible(v => ({ ...v, [s.key]: !v[s.key] }))}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.3rem 0.7rem',
                borderRadius: 'var(--radius-pill)',
                border: `1px solid ${visible[s.key] ? s.color : 'var(--line)'}`,
                background: 'var(--surface)',
                color: visible[s.key] ? 'var(--ink-soft)' : 'var(--muted)',
                opacity: visible[s.key] ? 1 : 0.6,
                cursor: 'pointer',
                fontSize: '0.78rem',
              }}
            >
              <span
                aria-hidden="true"
                style={{ width: '0.7rem', height: '0.7rem', borderRadius: '50%', background: s.color, display: 'inline-block' }}
              />
              {s.label}
              {s.approximate && (
                <span
                  data-testid={`series-approx-${s.key}`}
                  style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--muted)', background: 'var(--surface-alt)', border: '1px solid var(--line)', borderRadius: 'var(--radius-pill)', padding: '0.05rem 0.4rem' }}
                >
                  approximate
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Textual summary for screen readers — the chart in words */}
      <p data-testid="funnel-chart-summary" style={visuallyHiddenStyle}>
        Daily funnel totals for {periodLabel}:{' '}
        {totals
          .map(s => `${s.label} ${s.total.toLocaleString()}${s.approximate ? ' (approximate)' : ''}`)
          .join(', ')}
        .
      </p>
      {data.length === 0 ? (
        <p className="no-data">No data yet.</p>
      ) : (
        <>
          <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label={`Daily funnel chart, ${periodLabel}`}>
            {visibleSeries.map(s => (
              <polyline
                key={s.key}
                data-testid={`series-line-${s.key}`}
                points={linePoints(s.key)}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
              />
            ))}
            {/* A MARKER PER POINT, and it is not decoration.
                A polyline needs two coordinates to draw a segment, so a
                single-day period — "Today", or "This week" on a Monday —
                rendered every series as an invisible one-point line: legend
                chips, axis labels reading the same date twice, and an empty
                white box. The numbers were on the page the whole time, in the
                screen-reader summary above, which is visually hidden; a
                sighted publisher saw nothing.
                Markers also make individual days readable on a sparse series,
                which is most of them at this traffic. */}
            {visibleSeries.map(s =>
              pointsOf(s.key).map((p, i) => (
                <circle
                  key={`${s.key}-${i}`}
                  data-testid={`series-point-${s.key}`}
                  cx={p.x}
                  cy={p.y}
                  r={data.length === 1 ? 4 : 2.5}
                  fill={s.color}
                />
              ))
            )}
          </svg>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--muted)', fontSize: '0.75rem' }}>
            <span>{data[0].day}</span>
            <span>{data[data.length - 1].day}</span>
          </div>
        </>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// Per-feed network-reported EPC row: local edit buffer, PATCH on blur when
// both fields are valid and changed. The value is for the publisher's OWN
// reconciliation — nothing here feeds any computed earnings figure.
// -----------------------------------------------------------------------

function isValidEpc(amount: string, currency: string): { amount: number; currency: string } | null {
  // Blank never becomes 0: Number('') === 0, so a trimmed-empty check has to
  // happen BEFORE the Number() conversion below, or an unfilled amount field
  // paired with a valid currency would silently save as a real 0 EPC.
  const trimmedAmount = amount.trim();
  const trimmedCurrency = currency.trim();
  if (trimmedAmount === '' || trimmedCurrency === '') return null;

  const n = Number(trimmedAmount);
  if (!Number.isFinite(n) || n < 0 || n > 1000) return null;
  if (!/^[A-Za-z]{3}$/.test(trimmedCurrency)) return null;
  return { amount: n, currency: trimmedCurrency.toUpperCase() };
}

interface FeedEpcRowProps {
  feed: AnalyticsFeedRow;
  onSaved: () => void;
}

const FeedEpcRow: React.FC<FeedEpcRowProps> = ({ feed, onSaved }) => {
  const [amount, setAmount] = useState(feed.epcEstimate != null ? String(feed.epcEstimate) : '');
  const [currency, setCurrency] = useState(feed.epcCurrency || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setAmount(feed.epcEstimate != null ? String(feed.epcEstimate) : '');
    setCurrency(feed.epcCurrency || '');
  }, [feed.epcEstimate, feed.epcCurrency]);

  const commit = useCallback(async () => {
    // Nothing entered yet, or unchanged — nothing to save
    if (amount === '' && currency === '') return;
    const parsed = isValidEpc(amount, currency);
    if (!parsed) {
      setError(amount === '' || currency === '' ? 'Enter both an amount and a 3-letter currency to save' : 'Amount must be 0–1000; currency must be 3 letters');
      return;
    }
    if (parsed.amount === feed.epcEstimate && parsed.currency === feed.epcCurrency) return; // unchanged

    setError('');
    setSaving(true);
    try {
      await apiService.updateFeedEpc(feed.feedId, parsed.amount, parsed.currency);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [amount, currency, feed.feedId, feed.epcEstimate, feed.epcCurrency, onSaved]);

  // Clears a previously-set EPC back to unconfigured — paired nulls, never a
  // partial clear (PATCH /feeds/:id rejects one-null-one-set).
  const clear = useCallback(async () => {
    setError('');
    setSaving(true);
    try {
      await apiService.updateFeedEpc(feed.feedId, null, null);
      setAmount('');
      setCurrency('');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear');
    } finally {
      setSaving(false);
    }
  }, [feed.feedId, onSaved]);

  const hasEpc = feed.epcEstimate != null && feed.epcCurrency != null;

  return (
    <tr data-testid={`feed-epc-row-${feed.feedId}`}>
      <td>
        <strong>{feed.label}</strong>
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{feed.network}</div>
      </td>
      <td>{feed.clicks.toLocaleString()}</td>
      <td>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <input
            data-testid={`epc-amount-${feed.feedId}`}
            type="number"
            min={0}
            max={1000}
            step="0.01"
            value={amount}
            disabled={saving}
            onChange={e => setAmount(e.target.value)}
            onBlur={commit}
            style={{ width: '5.5rem', padding: '0.3rem 0.4rem', border: '1px solid var(--line)', borderRadius: 'var(--radius)' }}
          />
          <input
            data-testid={`epc-currency-${feed.feedId}`}
            type="text"
            maxLength={3}
            placeholder="USD"
            value={currency}
            disabled={saving}
            onChange={e => setCurrency(e.target.value.toUpperCase())}
            onBlur={commit}
            style={{ width: '3.5rem', padding: '0.3rem 0.4rem', border: '1px solid var(--line)', borderRadius: 'var(--radius)', textTransform: 'uppercase' }}
          />
          {hasEpc && (
            <button
              type="button"
              data-testid={`epc-clear-${feed.feedId}`}
              onClick={clear}
              disabled={saving}
              title="Clear this feed's EPC"
              style={{ padding: '0.2rem 0.5rem', border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: saving ? 'wait' : 'pointer', fontSize: '0.75rem', color: 'var(--muted)' }}
            >
              Clear
            </button>
          )}
          {saving && <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>saving…</span>}
        </div>
        {error && <div data-testid={`epc-error-${feed.feedId}`} style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: '0.25rem' }}>{error}</div>}
      </td>
    </tr>
  );
};

// -----------------------------------------------------------------------
// Measured earnings — money from the networks' own reports, and the
// conversion rate. NOTHING here is an estimate, and nothing here may ever
// be summed with one: a measurement and a guess are different claims about
// the world, and adding them produces a number that is neither.
// -----------------------------------------------------------------------

/**
 * Has ANY conversion been ingested for this publisher?
 *
 * Named because it gates two unrelated things — the measured panel's empty
 * state and whether the manual reconcile ledger exists at all — and because
 * `unattributedSummary` groups EVERY conversion including `attribution =
 * 'matched'`, despite its name. Adding the obvious-looking
 * `WHERE attribution <> 'matched'` to that query (matching what
 * `unattributedByNetwork` already does) would silently un-hide the reconcile
 * panel for every connected publisher. One definition, so that trap is sprung
 * in one place rather than two.
 */
function hasIngestedConversions(e: MeasuredEarningsResponse): boolean {
  return e.unattributed.some(r => r.count > 0);
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  reversed: 'Reversed',
  paid: 'Paid',
};

/** Sum per (currency, status) across feeds. Within ONE currency only — currencies never sum. */
function moneyByCurrencyAndStatus(rows: MeasuredEarningsResponse['measured']) {
  const map = new Map<string, { currency: string | null; status: string; commission: number; conversions: number }>();
  for (const r of rows) {
    const key = `${r.currency ?? '—'}:${r.status}`;
    const entry = map.get(key) ?? { currency: r.currency ?? null, status: r.status, commission: 0, conversions: 0 };
    entry.commission += r.commission;
    entry.conversions += r.conversions;
    map.set(key, entry);
  }
  return Array.from(map.values()).sort((a, b) =>
    (a.currency ?? '').localeCompare(b.currency ?? '') || a.status.localeCompare(b.status));
}

const MeasuredEarningsSection: React.FC<{ earnings: MeasuredEarningsResponse }> = ({ earnings }) => {
  const { measured, byNetwork, rate } = earnings;
  const hasConversions = hasIngestedConversions(earnings);

  // No measurement at all: an OFFER, never a measured figure of zero. But an
  // OFFER is the wrong message once a network is actually connected and has
  // synced — that publisher already did the thing this copy asks for, and
  // seeing it again reads as "your connection didn't work" when it did.
  if (!hasConversions && measured.length === 0) {
    return (
      <div data-testid="measured-panel" style={{ marginTop: '1.5rem' }}>
        <h3>Measured earnings</h3>
        {earnings.hasActiveConnection ? (
          <p className="no-data" data-testid="measured-empty-connected">
            Connected and checked{earnings.lastSyncedAt
              ? ` as of ${new Date(earnings.lastSyncedAt).toLocaleDateString()}`
              : ''} — no commissions reported yet. This updates automatically as your
            network reports sales.
          </p>
        ) : (
          <p className="no-data" data-testid="measured-empty-offer">
            Nothing measured yet. Connect a network account or upload its commissions report on
            the <a href="/networks">Networks page</a>, and the commissions it reports appear
            here — real numbers from the network, never a guess.
          </p>
        )}
      </div>
    );
  }

  const money = moneyByCurrencyAndStatus(measured);
  const unattributed = byNetwork.filter(r => r.count > 0);

  return (
    <div data-testid="measured-panel" style={{ marginTop: '1.5rem' }}>
      <h3>Measured earnings</h3>
      <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 0 }}>
        All time, measured from your networks&apos; own reports. A pending commission is not
        money yet, so each tier is shown on its own — and different currencies are never
        added together.
      </p>

      {money.length > 0 && (
        <table className="data-table" data-testid="measured-money">
          <thead>
            <tr>
              <th>Status</th>
              <th>Commission</th>
              <th>Sales it came from</th>
            </tr>
          </thead>
          <tbody>
            {money.map(row => (
              <tr key={`${row.currency}:${row.status}`} data-testid={`measured-${row.currency ?? 'none'}-${row.status}`}>
                <td style={{ padding: '0.4rem 1rem 0.4rem 0' }}>{STATUS_LABELS[row.status] ?? row.status}</td>
                <td style={{ padding: '0.4rem 1rem 0.4rem 0', fontWeight: 600 }}>
                  {row.commission.toFixed(2)} {row.currency ?? '(currency not reported)'}
                </td>
                <td style={{ padding: '0.4rem 0', color: 'var(--muted)' }}>
                  {row.conversions} {row.conversions === 1 ? 'sale' : 'sales'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* The rate. Withheld states never render a figure: below the floor a
          percentage is noise dressed as fact, and before ANY conversion has
          matched, "0.0%" is indistinguishable from a broken inbound path. The
          counts render at any volume — a count is true at any volume. */}
      <div style={{ marginTop: '0.75rem' }}>
        {/* Outer test stays on `rate.rate` because that is what TypeScript can
            narrow; the withheld REASON is then branched explicitly below rather
            than one of them falling into an unlabelled else. The type permits
            `rate: null` with `withheld: null` — an impossible fourth state that
            only a discriminated union in the contract could rule out. */}
        {rate.rate !== null ? (
          <p data-testid="measured-rate" style={{ margin: 0 }}>
            <strong>{(rate.rate * 100).toFixed(1)}%</strong> of your last {rate.windowDays} days&apos;{' '}
            {rate.attributableClicks} attributable clicks led to a reported sale
            ({rate.matchedClicks} {rate.matchedClicks === 1 ? 'click' : 'clicks'}).
            Attributable means clicks we tagged on networks that report back to us —
            clicks we cannot follow are not counted against you.
          </p>
        ) : (
          <p data-testid="measured-rate-withheld" style={{ margin: 0, color: 'var(--muted)' }}>
            {rate.withheld === 'low-volume' ? (
              <>
                Conversion rate: not enough attributable clicks yet for an honest percentage
                ({rate.attributableClicks} of the {rate.floor} needed in the last {rate.windowDays} days).
                The counts above are exact at any volume — a percentage from this small a sample is not.
              </>
            ) : (
              <>
                No conversions have been matched to your clicks yet
                ({rate.attributableClicks} attributable clicks in the last {rate.windowDays} days).
                Once a network reports a sale carrying one of our click tags, the rate appears here.
              </>
            )}
          </p>
        )}
      </div>

      {unattributed.length > 0 && (
        <div style={{ marginTop: '0.75rem' }} data-testid="measured-unattributed">
          <h4 style={{ marginBottom: '0.25rem' }}>Reported, but not traceable to a click</h4>
          {unattributed.map(row => (
            <p key={`${row.network}:${row.attribution}`} style={{ margin: '0.25rem 0', fontSize: '0.85rem' }}
               data-testid={`unattributed-${row.network}-${row.attribution}`}>
              <strong>{row.network}</strong>: {row.count}{' '}
              {row.count === 1 ? 'conversion' : 'conversions'}
              {row.explanation ? ` — ${row.explanation}` : ''}
            </p>
          ))}
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0.25rem 0 0' }}>
            Counted in your earnings above, never in the conversion rate.
          </p>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------

const AnalyticsPage: React.FC = () => {
  const [period, setPeriod] = useState<AnalyticsPeriod>(initialPeriod);
  const [data, setData] = useState<UserAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [copiedRef, setCopiedRef] = useState<string | null>(null);
  // Measured earnings are all-time with their own fixed rate window, so this
  // is fetched once, not per selected period. An enhancement to the page, not
  // a prerequisite: failing to load it must not hide the analytics.
  const [earnings, setEarnings] = useState<MeasuredEarningsResponse | null>(null);

  // Request-generation counter: a response for a period the user already
  // switched away from is discarded instead of clobbering the newer state.
  const requestIdRef = useRef(0);

  const fetchAnalytics = useCallback(async (p: AnalyticsPeriod) => {
    const requestId = ++requestIdRef.current;
    setError(null);
    try {
      const result = await apiService.getAnalytics(p);
      if (requestIdRef.current !== requestId) return; // superseded — discard
      setData(result);
    } catch (err) {
      if (requestIdRef.current !== requestId) return; // superseded — discard
      setError(err instanceof Error ? err.message : 'Could not load analytics.');
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchAnalytics(period);
  }, [period, fetchAnalytics]);

  useEffect(() => {
    apiService.getMeasuredEarnings()
      .then(setEarnings)
      .catch(err => console.error('Failed to load measured earnings:', err));
  }, []);

  // Retry must visibly re-enter the loading state, not just silently refetch
  // behind the still-shown error banner.
  const handleRetry = useCallback(() => {
    setLoading(true);
    fetchAnalytics(period);
  }, [period, fetchAnalytics]);

  const copyRef = useCallback(async (ref: string) => {
    try {
      await navigator.clipboard.writeText(ref);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = ref;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedRef(ref);
    setTimeout(() => setCopiedRef(null), 2000);
  }, []);

  if (loading && !data) {
    return <section className="stats-section"><p className="no-data">Loading analytics…</p></section>;
  }

  type ProductRow = { name: string; url: string | null; matches: number; clicks: number };
  const asProduct = (p: any): ProductRow => ({
    name: p.name || '',
    url: p.url || null,
    matches: p.matches || 0,
    clicks: p.clicks || 0,
  });
  const topProducts: ProductRow[] = (data?.topProducts || []).map(asProduct);
  const neverClicked: ProductRow[] = ((data as any)?.neverClicked || []).map(asProduct);

  return (
    <div data-testid="analytics-page">
      <section className="stats-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Analytics</h2>
          <div role="group" aria-label="Period" className="segmented">
            {PERIODS.map(({ key, short }) => (
              <button
                key={key}
                data-testid={`period-${key}`}
                onClick={() => { setPeriod(key); rememberPeriod(key); }}
                disabled={period === key}
                aria-pressed={period === key}

              >
                {short}
              </button>
            ))}
          </div>
        </div>

        {error ? (
          <div data-testid="analytics-error" style={errorBannerStyle}>
            <span>{error}</span>
            <button data-testid="analytics-retry" style={retryButtonStyle} onClick={handleRetry}>Retry</button>
          </div>
        ) : data ? (
          <>
            {/* Headline deltas vs the previous equal period (prominent for
                short periods where a day-over-day read is the point). */}
            {/* NOTHING TO COMPARE AGAINST IS A REAL ANSWER, and it was being
                rendered as growth. The previous window can begin before this
                account has data — the instrument was not running, or the account
                was not yet sending — and a delta from it divides a full window by
                a shorter one. Measured on the live account 2026-09-07: the 30d
                previous window (07-10..08-08) predates every series, and
                `Overlays viewed` showed ▲ +59% for 30 days of views against 21.
                Two of the three badges were artifacts and one was true, with
                nothing to tell them apart.

                The server decides this (`comparison.partial`) because the client
                cannot: for `week` the cycle is not the window length, so deriving
                the previous window's start here would be this repo's favourite
                bug — the same rule stated twice. */}
            {data.comparison?.partial && (
              <p className="no-data" data-testid="deltas-unavailable" style={{ marginBottom: '0.6rem' }}>
                No comparison yet: the period before this one starts before your data does
                {data.comparison.comparableSince
                  ? `, which begins ${new Date(`${data.comparison.comparableSince}T00:00:00Z`)
                      .toLocaleDateString(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' })}`
                  : ''}
                . The counts above are exact; a change against it would not be.
              </p>
            )}

            {data.comparison && !data.comparison.partial && (
              <div data-testid="period-deltas" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', marginBottom: '0.6rem', fontSize: '0.8rem' }}>
                <span style={{ color: 'var(--muted)' }}>vs {period === 'today' ? 'yesterday' : period === 'yesterday' ? 'the day before' : period === 'week' ? 'last week' : 'the previous period'}:</span>
                <span data-testid="delta-clicks">Product clicks <DeltaBadge current={data.totalClicks} previous={data.comparison.clicks ?? 0} /></span>
                <span data-testid="delta-views">Overlays viewed <DeltaBadge current={data.totalViews} previous={data.comparison.views ?? 0} /></span>
                <span data-testid="delta-impressions">Pageviews <DeltaBadge current={data.totalImpressions ?? 0} previous={data.comparison.impressions ?? 0} /></span>
                <span data-testid="delta-matches">Overlays delivered <DeltaBadge current={data.totalMatches} previous={data.comparison.matches ?? 0} /></span>
              </div>
            )}

            {/* Which clock these days were counted in (analytics-timezone).
                Publishers read this page next to Google Analytics, which counts
                days in the timezone set on their GA property — so the same day
                legitimately holds different numbers in the two tools. Saying so
                turns "your numbers are wrong" into "these are on a different
                clock", which a publisher can reconcile themselves. */}
            <div data-testid="analytics-timezone-note" style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.75rem' }}>
              Days are counted in <strong style={{ fontWeight: 600 }}>{data.timezone ?? 'UTC'}</strong>.
              {' '}Google Analytics uses the timezone set on your GA property, so days may start and end at a different hour there.
            </div>

            {/* Funnel tiles + always-visible status (cutoff note, power gate) */}
            <FunnelSection daily={data.daily} viewTrackingStartedAt={data.viewTrackingStartedAt} periodLabel={periodLabelFor(period)} />

            {/* The ONE caveat disclosure for this page */}
            <AboutMetricsDisclosure viewTrackingStartedAt={data.viewTrackingStartedAt} />

            {/* The one daily chart — four raw-count series */}
            <FunnelChart data={data.daily} periodLabel={periodLabelFor(period)} />

            {/* TWO lists, because one was answering neither question.
                It was ordered by MATCHES under a "Top Performing" heading — so
                the most-shown, never-clicked product sat at the top of a success
                list. Performance is clicks; being shown and ignored is the other
                story, and it is the one a publisher can act on. */}
            <div className="top-products" data-testid="top-products">
              <h3>Most clicked</h3>
              {topProducts.length === 0 ? (
                <p className="no-data">
                  Nothing has been clicked yet in this period. Overlays have to be seen before
                  anything can perform — the funnel above shows whether they are being served.
                </p>
              ) : (
                <div className="products-list">
                  {topProducts.map((product, index) => (
                    <div key={index} className="product-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="product-name">
                        {product.url ? (
                          <a href={product.url} target="_blank" rel="noopener noreferrer">{product.name}</a>
                        ) : product.name}
                      </span>
                      {/* THE ONLY NUMBERS ON THIS PAGE THAT WERE NEITHER
                          THOUSANDS-SEPARATED NOR PLURAL-AGREED. Ten other
                          figures here go through `toLocaleString`; these four
                          did not, and on the live account that reads "1 clicks"
                          on all five rows of the success list and "1160 shows"
                          beside a funnel tile saying "1,997". Both were
                          invisible until the mock could produce the real shape —
                          the old fixture gave every row 7 to 23 clicks, which no
                          account here has. */}
                      <span className="click-count">{plural(product.clicks, 'click')}</span>
                      <span className="match-count">from {plural(product.matches, 'show')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {neverClicked.length > 0 && (
              <div className="top-products" data-testid="never-clicked-products">
                <h3>Shown often, never clicked</h3>
                {/* Analytics is deliberately a read-only surface (screens/analytics.md,
                    Decisions 2026-09-05) — so where it produces a finding it has to say
                    which screen owns the fix. This list was the one place that produced
                    a finding and pointed nowhere. */}
                <p className="no-data">
                  Each of these took an overlay slot and returned nothing. Worth checking the
                  price, the image, or whether the product suits the posts it appears on —
                  the feed they came from is on <Link to="/feeds">Product feeds</Link>.
                </p>
                <div className="products-list">
                  {neverClicked.map((product, index) => (
                    <div key={index} className="product-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="product-name">
                        {product.url ? (
                          <a href={product.url} target="_blank" rel="noopener noreferrer">{product.name}</a>
                        ) : product.name}
                      </span>
                      <span className="match-count">{plural(product.matches, 'show')}</span>
                      <span className="click-count">0 clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Measured money and the conversion rate, beside the funnel that
                explains them. Renders nothing while the earnings read is
                pending or failed — an enhancement, not a prerequisite. */}
            {earnings && <MeasuredEarningsSection earnings={earnings} />}

            {/* Reconciliation — optional, collapsed by default. The evidence
                layer for TRUE EPC: click refs → network conversion report.
                Nothing here computes or renders an earnings figure.

                Shown only while NO conversions have been ingested: for a
                connected publisher this panel asks them to redo by hand a join
                the measured panel above already performed in SQL. For an
                unconnected one it is the only evidence path they have. */}
            {!(earnings && hasIngestedConversions(earnings)) && (
            <div style={{ marginTop: '1.5rem' }}>
              <button
                type="button"
                data-testid="reconcile-toggle"
                aria-expanded={reconcileOpen}
                aria-controls="reconcile-content"
                onClick={() => setReconcileOpen(o => !o)}
                style={{ minHeight: '44px', padding: '0.5rem 1rem', background: 'var(--surface)', color: 'var(--brand)', border: '1px solid var(--brand)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
              >
                Reconcile with your network (optional) {reconcileOpen ? '▲' : '▼'}
              </button>
              {reconcileOpen && (
                <div id="reconcile-content" data-testid="reconcile-content" style={{ ...cardStyle, marginTop: '0.75rem' }}>
                  <p className="no-data" style={{ marginTop: 0 }}>
                    Match these references against your network's conversion report to reconcile clicks,
                    and record the EPC your network reports for your own bookkeeping.
                  </p>

                  <h4 style={{ marginBottom: '0.5rem' }}>Latest 50 click references</h4>
                  <div data-testid="click-ledger">
                    {data.recentClicks.length === 0 ? (
                      <p className="no-data">No clicks recorded yet.</p>
                    ) : (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Click ref</th>
                            <th>Feed</th>
                            <th>Network</th>
                            <th>When</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {data.recentClicks.map(c => (
                            <tr key={c.clickRef ?? `${c.feedId}-${c.createdAt}`} data-testid={`click-row-${c.clickRef ?? 'none'}`}>
                              <td style={{ padding: '0.4rem', fontFamily: 'ui-monospace, monospace' }}>
                                {c.clickRef ?? <span style={{ color: 'var(--muted)' }}>—</span>}
                              </td>
                              <td style={{ padding: '0.4rem' }}>{c.feedLabel}</td>
                              <td style={{ padding: '0.4rem' }}>{c.network}</td>
                              <td style={{ padding: '0.4rem' }}>{new Date(c.createdAt).toLocaleString()}</td>
                              <td style={{ padding: '0.4rem' }}>
                                {c.clickRef && (
                                  <button
                                    data-testid={`copy-click-ref-${c.clickRef}`}
                                    onClick={() => copyRef(c.clickRef!)}
                                    className="btn btn--secondary"
                                  >
                                    {copiedRef === c.clickRef ? 'Copied!' : 'Copy'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <h4 style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}>Network-reported EPC (per feed)</h4>
                  <p className="no-data" style={{ marginTop: 0 }}>
                    Enter the EPC your network reports, in its own currency — for your reconciliation only.
                  </p>
                  <table className="data-table" data-testid="feed-epc-table">
                    <thead>
                      <tr>
                        <th style={{ padding: '0.5rem' }}>Feed</th>
                        <th style={{ padding: '0.5rem' }}>Product clicks ({periodLabelFor(period)})</th>
                        <th style={{ padding: '0.5rem' }}>Network-reported EPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.perFeed.map(feed => (
                        <FeedEpcRow key={feed.feedId} feed={feed} onSaved={() => fetchAnalytics(period)} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            )}
          </>
        ) : null}
      </section>
    </div>
  );
};

export default AnalyticsPage;
